import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { useCart, useCurrency, formatPrice, itemPrice } from "@/lib/cart";

export const Route = createFileRoute("/carrito")({
  head: () => ({
    meta: [
      { title: "Carrito | Mine Class" },
      { name: "description", content: "Finaliza la compra de tus cursos Mine Class." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Page,
});

function Page() {
  const cart = useCart();
  const { currency, setCurrency } = useCurrency();
  const [form, setForm] = useState({ email: "", phone: "", first_name: "", last_name: "", doc: "" });
  const [accepted, setAccepted] = useState(false);
  const [sent, setSent] = useState(false);

  const total = cart.items.reduce((s, i) => s + itemPrice(i, currency) * i.qty, 0);
  const canPay = accepted && cart.items.length > 0 && form.email && form.first_name && form.last_name && form.doc;

  return (
    <div className="bg-white text-[var(--color-on-surface)] min-h-screen flex flex-col">
      <SiteNav />
      <main className="flex-1 py-xl">
        <div className="max-w-container-max mx-auto px-margin-desktop">
          <h1 className="text-3xl font-bold text-[var(--color-brand-purple)] mb-2">Carrito</h1>
          <p className="text-sm text-[var(--color-on-surface-variant)] mb-6">
            Revisa tus cursos, completa tus datos y elige tu medio de pago.
          </p>

          {cart.items.length === 0 && !sent ? (
            <div className="bg-[var(--color-surface-container-low)] border border-dashed border-[var(--color-outline-variant)] p-10 text-center rounded-md">
              <p className="text-[var(--color-on-surface-variant)] mb-4">Tu carrito está vacío.</p>
              <Link to="/cursos/personas" className="inline-block px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold text-xs uppercase tracking-wider rounded-md">
                Ver cursos
              </Link>
            </div>
          ) : sent ? (
            <div className="bg-green-50 border border-green-200 p-8 rounded-md">
              <h2 className="text-lg font-bold text-green-800 mb-2">¡Solicitud enviada!</h2>
              <p className="text-sm text-green-700">Nos pondremos en contacto para coordinar el pago y activar tus cursos.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <section className="bg-white border border-[var(--color-outline-variant)] rounded-md p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold">Tus cursos</h2>
                    <div className="inline-flex border border-[var(--color-outline-variant)] rounded-md overflow-hidden text-xs font-bold">
                      <button onClick={() => setCurrency("CLP")} className={`px-3 py-1 ${currency === "CLP" ? "bg-[var(--color-brand-purple)] text-white" : "bg-white"}`}>CLP $</button>
                      <button onClick={() => setCurrency("USD")} className={`px-3 py-1 ${currency === "USD" ? "bg-[var(--color-brand-purple)] text-white" : "bg-white"}`}>USD US$</button>
                    </div>
                  </div>
                  <ul className="divide-y divide-[var(--color-outline-variant)]">
                    {cart.items.map((i) => (
                      <li key={i.id} className="py-3 flex items-center gap-3">
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{i.title}</div>
                          <div className="text-xs text-[var(--color-on-surface-variant)]">{formatPrice(i, currency)}</div>
                        </div>
                        <input
                          type="number"
                          min={1}
                          value={i.qty}
                          onChange={(e) => cart.setQty(i.id, parseInt(e.target.value) || 1)}
                          className="w-16 border border-[var(--color-outline-variant)] px-2 py-1 text-sm rounded"
                        />
                        <button onClick={() => cart.remove(i.id)} className="text-red-600 text-xs underline">Quitar</button>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="bg-white border border-[var(--color-outline-variant)] rounded-md p-5">
                  <h2 className="font-bold mb-1">Información de contacto</h2>
                  <p className="text-xs text-[var(--color-on-surface-variant)] mb-4">Verifica que la información sea correcta.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Correo electrónico">
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[var(--color-outline-variant)] px-3 py-2 rounded" />
                    </Field>
                    <Field label="Teléfono">
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[var(--color-outline-variant)] px-3 py-2 rounded" />
                    </Field>
                    <Field label="Nombre(s)">
                      <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border border-[var(--color-outline-variant)] px-3 py-2 rounded" />
                    </Field>
                    <Field label="Apellidos">
                      <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border border-[var(--color-outline-variant)] px-3 py-2 rounded" />
                    </Field>
                    <Field label="DNI o Pasaporte">
                      <input value={form.doc} onChange={(e) => setForm({ ...form, doc: e.target.value })} className="w-full border border-[var(--color-outline-variant)] px-3 py-2 rounded" />
                    </Field>
                  </div>
                  <label className="flex items-start gap-2 mt-4 text-sm">
                    <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" />
                    <span>
                      Al continuar con la compra estás aceptando nuestros{" "}
                      <a href="#" className="text-[var(--color-brand-coral)] underline">Términos y Condiciones</a>.
                    </span>
                  </label>
                </section>

                <section className="bg-white border border-[var(--color-outline-variant)] rounded-md p-5">
                  <h2 className="font-bold mb-1">Pago</h2>
                  <p className="text-xs text-[var(--color-on-surface-variant)] mb-4">El pago se realiza en sistemas seguros y encriptados.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <button
                      disabled={!canPay}
                      onClick={() => setSent(true)}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-[#00b1ea] bg-white text-[#003087] font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#e6f7fd]"
                    >
                      Pagar con Mercado Pago
                    </button>
                    <button
                      disabled={!canPay}
                      onClick={() => setSent(true)}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-[#ec1c24] bg-white text-[#ec1c24] font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#fdecec]"
                    >
                      Pagar con Webpay
                    </button>
                  </div>
                  {!accepted && cart.items.length > 0 && (
                    <p className="text-xs text-red-600 mt-3">Debes aceptar los Términos y Condiciones para continuar.</p>
                  )}
                </section>
              </div>

              <aside className="bg-[var(--color-surface-container-low)] border border-[var(--color-outline-variant)] rounded-md p-5 h-fit sticky top-24">
                <h2 className="font-bold mb-4">Resumen</h2>
                <div className="flex justify-between text-sm mb-2">
                  <span>Cursos</span>
                  <span>{cart.count}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t border-[var(--color-outline-variant)] pt-3 mt-3">
                  <span>Total</span>
                  <span>{currency === "CLP" ? `$${total.toLocaleString("es-CL")} CLP` : `US$${total.toLocaleString("en-US")}`}</span>
                </div>
                <p className="text-[11px] text-[var(--color-on-surface-variant)] mt-2">
                  Los cursos sin precio publicado se coordinan directamente por contacto.
                </p>
              </aside>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">{label}</div>
      {children}
    </label>
  );
}
